import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Avatar,
  Badge,
  Box,
  ButtonBase,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { CATEGORY_SETS_LABEL } from '../common/constants';
import {
  DbQuestionData,
  DetailedPlayer,
  Maintenance,
} from '../components/room/types';
import {
  roundToOneDecimal,
  utcToLocalDateTime,
} from '../components/room/utils';
import { timeSince } from '@par/shared/utils/date-time';
import { fetchGet, fetchPost } from '../api/common';
import Countdown, { zeroPad } from 'react-countdown';
import { useNavigate } from 'react-router-dom';
import { ScoreCardModal } from '../modals/ScoreCardModal';
import { StyledPaperItem } from '../common/mui-components';
import { resizeImage } from '../api/cloudinary';
import { GameDb } from '@par/shared/common/types';
import { SearchBarMain } from '../components/header/SearchBarMain';
import { Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';

const NoWrapText = styled(Typography)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

type AdminViewUser = {
  id: string;
  avatarPublicId: string;
  calibrated: boolean;
  rankedRating: number;
  ghostChallengeResponseRate: number;
  ghostChallengeReceivedSentRatio: number;
  username: string;
};

type RecommendedUser = {
  id: string;
  calibrated: boolean;
  avatarPublicId: string;
  username: string;
  rankedRating: number;
  ghostChallengeStatus: 'yes' | 'no' | 'maybe';
  ghostChallengeResponseRate: number;
  ghostChallengeReceivedSentRatio: number;
  ghostChallengesPlayed: number;
  ghostChallengeLastReceivedAt: number;
};

type GhostChallengeRecord = {
  profileId: string;
  opponentProfileId: string;
  opponentAvatarPublicId: string;
  opponentUsername: string;
  opponentGhostChallengeStatus: string;
  wins: number;
  losses: number;
  ties: number;
};

interface GhostChallengeData {
  convertedToSolo: boolean;
  createdAt: number;
  createdBy: string;
  completedAt: number;
  expiresAt: number;
  expired: boolean;
  fromAvatarPublicId: string;
  fromProfileId: string;
  fromUsername: string;
  fromUserRankedRating: number;
  gameId: string;
  toAvatarPublicId: string;
  toProfileId: string;
  toUsername: string;
  toUserRankedRating: number;
}

export interface GhostChallenge extends Game {
  ghostChallengeData: GhostChallengeData;
}

export type GhostChallengConfig = {
  categorySet: string;
  gameType: 'casual' | 'pro';
  ghostChallenge?: boolean;
  ghostChallengeFrom?: string;
  ghostChallengeTo?: string;
  ranked: boolean;
};

type RecommendationsFilter =
  | 'been-a-while'
  | 'similarily-matched'
  | 'active-haunters'
  | 'maybes';

type PropTypes = {
  close: () => void;
  ghostChallengeStatus: 'yes' | 'no' | 'maybe';
  handleGhostChallenge: (player: {
    profileId: string;
    username: string;
    ghostChallengeStatus: string;
  }) => void;
  handlePlayGhostMatchClick: (
    gameId: string,
    ghostChallengeFrom: string,
    ghostChallengeTo: string
  ) => void;
  isAdmin: boolean;
  maintenance: Maintenance;
  onUpdateProfile: () => void;
  open: boolean;
  profile: Profile;
};

export const GhostChallengesViewDialog = ({
  close,
  ghostChallengeStatus,
  handleGhostChallenge,
  handlePlayGhostMatchClick,
  isAdmin,
  maintenance,
  onUpdateProfile,
  open,
  profile,
}: PropTypes) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [viewProfile, setViewProfile] = useState<Profile | AdminViewUser>();
  const [status, setStatus] = useState(ghostChallengeStatus || 'maybe');
  const [recentGhostChallenges, setRecentGhostChallenges] =
    useState<GameDb[]>();
  const [ghostChallengesReceived, setGhostChallengesReceived] =
    useState<GhostChallenge[]>();
  const [ghostChallengesSent, setGhostChallengesSent] =
    useState<GhostChallenge[]>();
  const [recommendationsFilter, setRecommendationsFilter] =
    useState<RecommendationsFilter>('been-a-while');
  const [ghostRecommendations, setGhostRecommendations] =
    useState<RecommendedUser[]>();
  const [ghostChallengeRecords, setGhostChallengeRecords] =
    useState<GhostChallengeRecord[]>();
  const [openScorecardModal, setOpenScorecardModal] = useState(false);
  const [gameResults, setGameResults] = useState<
    Game & { questionSet: DbQuestionData[] }
  >();
  const [users, setUsers] = useState<AdminViewUser[]>();
  const [adminViewSelectedUser, setAdminViewSelectedUser] =
    useState<AdminViewUser>();
  const [winRatePlayId, setWinRatePlayId] = useState<string>();
  const [lastViewedChallengeIndex, setLastViewedChallengeIndex] =
    useState<number>();

  useEffect(() => {
    if (!open) {
      setViewProfile(undefined);
      return;
    }
    setViewProfile(profile);
    setRecommendationsFilter('been-a-while');
  }, [open]);

  useEffect(() => {
    if (!adminViewSelectedUser) return;
    setViewProfile(adminViewSelectedUser);
  }, [adminViewSelectedUser]);

  useEffect(() => {
    if (!viewProfile) return;
    fetchRecentGhostChallenges();
    fetchGhostChallengesReceived();
    fetchGhostChallengesSent();
    fetchGhostChallengeRecommendations('been-a-while');
    fetchGhostChallengeRecords();
    if (isAdmin) {
      fetchUsers();
    }
    postGhostChallengeConvertToSolo();
  }, [viewProfile]);

  const handleDialogClose = () => {
    close();
    onUpdateProfile();
  };

  const fetchRecentGhostChallenges = async () => {
    const res: GameDb[] = await fetchGet(
      `/games/ghost-challenges/recent/${viewProfile?.id}/10`
    );
    setRecentGhostChallenges((prev) => res);
    if (!res.length) return;
    const lastViewedIndex = res.findIndex(
      (g) => g.gameId === profile.ghostChallengeLastViewed
    );
    setLastViewedChallengeIndex(lastViewedIndex);
    const lastViewedGameId = res[0].gameId;
    saveLastViewedChallenge(lastViewedGameId);
    await fetchPost(`/profile/ghost-challenge-last-viewed/${viewProfile?.id}`, {
      ghostChallengeLastViewed: lastViewedGameId,
    });
  };

  const saveLastViewedChallenge = (gameId: string) => {
    console.log('saveLastViewedChallenge', gameId);
  };

  const fetchGhostChallengesReceived = async () => {
    const res: GhostChallenge[] = await fetchGet(
      `/games/ghost-challenges/received-sent/received/${viewProfile?.id}`
    );
    setGhostChallengesReceived((prev) => res);
  };

  const fetchGhostChallengesSent = async () => {
    const res: GhostChallenge[] = await fetchGet(
      `/games/ghost-challenges/received-sent/sent/${viewProfile?.id}`
    );
    setGhostChallengesSent((prev) => res);
  };

  const fetchGhostChallengeRecommendations = async (
    filter: RecommendationsFilter
  ) => {
    let res: RecommendedUser[] = [];
    if (filter === 'been-a-while') {
      res = await fetchGet(
        `/games/ghost-challenges/recommendations/been-a-while/${viewProfile?.id}`
      );
    }
    if (filter === 'similarily-matched') {
      res = await fetchGet(
        `/games/ghost-challenges/recommendations/similarily-matched/${viewProfile?.id}`
      );
    }
    if (filter === 'active-haunters') {
      res = await fetchGet(
        `/games/ghost-challenges/recommendations/active-haunters/${viewProfile?.id}`
      );
    }
    if (filter === 'maybes') {
      res = await fetchGet(
        `/games/ghost-challenges/recommendations/maybes/${viewProfile?.id}`
      );
    }
    setGhostRecommendations((prev) => shuffleAndLimit(res, 3));
  };

  const fetchGhostChallengeRecords = async () => {
    const res: GhostChallengeRecord[] = await fetchGet(
      `/games/ghost-challenges/records/${viewProfile?.id}`
    );
    const sorted = res.sort(
      (a, b) => b.wins + b.losses + b.ties - (a.wins + a.losses + a.ties)
    );
    setGhostChallengeRecords((prev) => sorted);
  };

  const fetchUsers = async () => {
    const res = await fetchGet(`/admin/users`);
    setUsers((prev) => res);
  };

  const postGhostChallengeConvertToSolo = async () => {
    const res = await fetchPost(
      `/games/ghost-challenges/convert-to-solo/${viewProfile?.id}`,
      {}
    );
  };

  const handleStatusChange = async (event: SelectChangeEvent) => {
    setStatus(event.target.value as 'yes' | 'no' | 'maybe');
    const res = await fetchPost(
      `/profile/ghost-challenge-status/${viewProfile?.id}`,
      {
        ghostChallengeStatus: event.target.value,
      }
    );
    onUpdateProfile();
  };

  const handleAdminViewChange = (user: any) => {
    const adminViewUser = users?.find((u) => u.id === user._id);
    setAdminViewSelectedUser(adminViewUser);
  };

  const getOpponent = (game: GameDb) => {
    const players = Object.values(game.players);
    const opponent = players.find((p) => p.profileId !== viewProfile?.id);
    return opponent;
  };

  const countdownRenderer = ({ days, hours, minutes, seconds }: any) => {
    return (
      <span style={{ color: theme.palette.warning.main }}>
        {days}:{zeroPad(hours)}:{zeroPad(minutes)}:{zeroPad(seconds)}
      </span>
    );
  };

  const handleExpireComplete = () => {
    fetchGhostChallengesReceived();
  };

  const results = (game: GhostChallenge) => (
    <Typography variant="body2">
      {utcToLocalDateTime(game.ghostChallengeData.completedAt)}
    </Typography>
  );

  const handleViewResultsClick = async (gameId: string) => {
    const res = await fetchGet(`/games/result/${gameId}`);
    res.game.questionSet = res.questionSet;
    setGameResults(res.game);
    setOpenScorecardModal(true);
  };

  const shuffleAndLimit = (array: any[], limit: number) =>
    array.sort(() => 0.5 - Math.random()).slice(0, limit);

  const handleUsernameClick = async (e: any, profileId: string) => {
    e.stopPropagation();
    close();
    navigate(`/profile/overview/${profileId}`);
  };

  const handleRecommendationsFilter = (e: any) => {
    const filter = e.target.value as RecommendationsFilter;
    setRecommendationsFilter(filter);
    fetchGhostChallengeRecommendations(filter);
  };

  const gameInfoTooltip = (game: GhostChallenge, received: boolean) => (
    <Stack>
      <Typography variant="body1">
        Player:{' '}
        <strong>
          {received
            ? game.ghostChallengeData.fromUsername
            : game.ghostChallengeData.toUsername}
        </strong>
      </Typography>
      <Typography variant="body1" sx={{ color: theme.palette.ranked.main }}>
        MMR:{' '}
        <strong>
          {received
            ? Math.floor(game.ghostChallengeData.fromUserRankedRating)
            : Math.floor(game.ghostChallengeData.toUserRankedRating)}
        </strong>
      </Typography>
      <Typography variant="body1">
        Game Name: <strong>{game.customGameName}</strong>
      </Typography>
      <Typography variant="body1">
        Ranked: <strong>{game.ranked ? 'Yes' : 'No'}</strong>
      </Typography>
      <Typography variant="body1">
        Diff: <strong>{game.gameType}</strong>
      </Typography>
      <Typography variant="body1">
        Category: <strong>{CATEGORY_SETS_LABEL[game.categorySet]}</strong>
      </Typography>
      <Typography variant="body1">
        Sent: <strong>{utcToLocalDateTime(game.createdAt)}</strong>
      </Typography>
    </Stack>
  );

  const prevGameInfoTooltip = (game: GameDb) => (
    <Stack>
      <Typography variant="body1">
        Player: <strong>{getOpponent(game)?.username}</strong>
      </Typography>
      <Typography variant="body1">
        Name: <strong>{game.customGameName}</strong>
      </Typography>
      <Typography variant="body1">
        Ranked: <strong>{game.ranked ? 'Yes' : 'No'}</strong>
      </Typography>
      <Typography variant="body1">
        Diff: <strong>{game.gameType}</strong>
      </Typography>
      <Typography variant="body1">
        Category: <strong>{CATEGORY_SETS_LABEL[game.categorySet]}</strong>
      </Typography>
      <Typography variant="body1">
        Played: <strong>{utcToLocalDateTime(game.createdAt)}</strong>
      </Typography>
    </Stack>
  );

  const getProgressValue = (record: GhostChallengeRecord) => {
    if (!ghostChallengeRecords) return 0;
    const totalMatches = record.wins + record.losses + record.ties;
    const highestMatchTotal =
      ghostChallengeRecords[0].wins +
      ghostChallengeRecords[0].losses +
      ghostChallengeRecords[0].ties;
    return (totalMatches / highestMatchTotal) * 100;
  };

  return (
    <>
      {gameResults ? (
        <ScoreCardModal
          onClose={() => setOpenScorecardModal(false)}
          categorySet={gameResults.categorySet}
          gameId={gameResults.gameId}
          gameType={gameResults.gameType}
          openScordcardModal={openScorecardModal}
          mode={gameResults.mode}
          players={gameResults.players as Record<string, DetailedPlayer>}
          profileId={Object.values(gameResults.players)[0].profileId}
          questionSet={gameResults.questionSet}
          ranked={gameResults.ranked}
        ></ScoreCardModal>
      ) : null}
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          {isAdmin ? (
            <Box position={'absolute'} left={24}>
              <SearchBarMain
                headerHeight={88}
                handleSelect={handleAdminViewChange}
              />
            </Box>
          ) : null}
          <Typography
            variant="h4"
            textAlign={'center'}
            sx={{
              textShadow:
                theme.palette.mode === 'dark'
                  ? `0 0 0px ${theme.palette.warning.main}, 0 0 0px ${theme.palette.warning.main}, 0px 0px 16px ${theme.palette.warning.main}`
                  : 'none',
            }}
          >
            Ghost Challenges
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ width: '100%', height: '100%' }}>
          <Box
            sx={{
              position: 'relative',
              flexGrow: 1,
              marginTop: 2,
            }}
          >
            <Grid container spacing={2} className="CONTAINER-MAIN">
              <Grid item xs={12} sm={4} className="CONTAINER-LEFT">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} className="PROFILE">
                    <StyledPaperItem style={{ height: '100%' }}>
                      <Stack spacing={2}>
                        {/* Header */}
                        <Stack
                          direction={'row'}
                          alignItems={'center'}
                          spacing={2}
                          paddingX={{ xs: 1, md: 2 }}
                        >
                          <Avatar
                            src={
                              viewProfile
                                ? resizeImage(
                                    viewProfile.avatarPublicId,
                                    200,
                                    200
                                  )
                                : ''
                            }
                            sx={{ borderRadius: 1 }}
                          />
                          <NoWrapText variant="h6led">
                            {viewProfile?.username}
                          </NoWrapText>
                        </Stack>
                        {/* Status */}
                        {!adminViewSelectedUser ? (
                          <FormControl size="small">
                            <InputLabel id="demo-select-small">
                              Status
                            </InputLabel>
                            <Select
                              value={status}
                              label="Status"
                              onChange={handleStatusChange}
                            >
                              <MenuItem value={'maybe'}>
                                May or may not respond to challenges
                              </MenuItem>
                              <MenuItem value={'yes'}>
                                Send me challenges!
                              </MenuItem>
                              <MenuItem value={'no'}>
                                Not accepting challenges right now
                              </MenuItem>
                            </Select>
                          </FormControl>
                        ) : null}
                        {/* Stats */}
                        <Stack spacing={0.5}>
                          <Stack
                            direction={'row'}
                            justifyContent={'space-between'}
                            paddingX={{ xs: 1, md: 2 }}
                            sx={{ cursor: 'default' }}
                          >
                            <Typography color={'ranked.main'}>MMR</Typography>
                            <Typography color={'ranked.main'}>
                              {viewProfile?.calibrated
                                ? Math.floor(viewProfile?.rankedRating)
                                : 'calibrating'}
                            </Typography>
                          </Stack>
                          <Tooltip
                            title="High response rate means you respond to most haunts sent to you"
                            placement="top"
                          >
                            <Stack
                              direction={'row'}
                              justifyContent={'space-between'}
                              paddingX={{ xs: 1, md: 2 }}
                              sx={{ cursor: 'default' }}
                            >
                              <NoWrapText>Response rate</NoWrapText>
                              <Typography>
                                {viewProfile
                                  ? viewProfile.ghostChallengeResponseRate *
                                      100 +
                                    '%'
                                  : 'N/A'}
                              </Typography>
                            </Stack>
                          </Tooltip>
                          <Tooltip
                            title="A haunt ratio of 50% means you send and received the exact same number of haunts. A low haunt ratio means you send more haunts than you receive. Which is awesome for PAR!"
                            placement="top"
                          >
                            <Stack
                              direction={'row'}
                              justifyContent={'space-between'}
                              paddingX={{ xs: 1, md: 2 }}
                              sx={{ cursor: 'default' }}
                            >
                              <NoWrapText>Haunt rate</NoWrapText>
                              <Typography>
                                {viewProfile
                                  ? (
                                      viewProfile.ghostChallengeReceivedSentRatio *
                                      100
                                    ).toFixed(0) + '%'
                                  : 'N/A'}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </StyledPaperItem>
                  </Grid>

                  <Grid item xs={12} sm={12} className="HAUNTED-BY">
                    <StyledPaperItem style={{ height: '100%' }}>
                      <Tooltip title="Showing past 10 days" placement="top">
                        <Typography variant="h6" mb={2}>
                          Haunted By
                        </Typography>
                      </Tooltip>
                      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table
                          stickyHeader
                          aria-label="simple table"
                          size="small"
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell width={'20%'}>From</TableCell>
                              <TableCell width={'10%'}>Status</TableCell>
                              <TableCell width={'13%'}>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ghostChallengesReceived?.length === 0 ? (
                              <TableRow
                                sx={{
                                  '&:last-child td, &:last-child th': {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell colSpan={3}>
                                  <Typography
                                    variant="body1"
                                    textAlign={'center'}
                                    fontStyle={'italic'}
                                    mt={2}
                                    mb={2}
                                  >
                                    No haunts to display
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              ghostChallengesReceived?.map((game) => (
                                <Tooltip
                                  title={gameInfoTooltip(game, true)}
                                  placement="right"
                                >
                                  <TableRow
                                    hover
                                    key={game.gameId}
                                    sx={{
                                      '&:last-child td, &:last-child th': {
                                        border: 0,
                                      },
                                    }}
                                  >
                                    <TableCell>
                                      <IconButton
                                        onClick={(e) =>
                                          handleUsernameClick(
                                            e,
                                            game.ghostChallengeData
                                              .fromProfileId
                                          )
                                        }
                                      >
                                        <Avatar
                                          src={resizeImage(
                                            game.ghostChallengeData
                                              .fromAvatarPublicId,
                                            50,
                                            50
                                          )}
                                          variant="rounded"
                                          sx={{
                                            margin: '2px',
                                            height: 35,
                                            width: 35,
                                          }}
                                        />
                                      </IconButton>
                                    </TableCell>
                                    <TableCell>
                                      {game.ghostChallengeData
                                        .convertedToSolo ? (
                                        'Expired'
                                      ) : game.ghostChallengeData
                                          .completedAt ? (
                                        'Completed'
                                      ) : (
                                        <Countdown
                                          date={
                                            game.ghostChallengeData.expiresAt
                                          }
                                          renderer={countdownRenderer}
                                          onComplete={handleExpireComplete}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {game.ghostChallengeData.completedAt ||
                                      game.ghostChallengeData.expired ? (
                                        results(game)
                                      ) : (
                                        <Button
                                          variant="contained"
                                          onClick={() =>
                                            handlePlayGhostMatchClick(
                                              game.gameId,
                                              game.ghostChallengeData
                                                .fromProfileId,
                                              game.ghostChallengeData
                                                .toProfileId
                                            )
                                          }
                                          fullWidth
                                          disabled={
                                            maintenance.scheduled ||
                                            !!adminViewSelectedUser
                                          }
                                        >
                                          Play
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                </Tooltip>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </StyledPaperItem>
                  </Grid>
                  <Grid item xs={12} sm={12} className="HAUNTS-SENT">
                    <StyledPaperItem style={{ height: '100%' }}>
                      <Tooltip title="Showing past 10 days" placement="top">
                        <Typography variant="h6" mb={2}>
                          Haunts Sent
                        </Typography>
                      </Tooltip>
                      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table
                          stickyHeader
                          aria-label="simple table"
                          size="small"
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell width={'20%'}>To</TableCell>
                              <TableCell width={'10%'}>Status</TableCell>
                              <TableCell width={'13%'}>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ghostChallengesSent?.length === 0 ? (
                              <TableRow
                                sx={{
                                  '&:last-child td, &:last-child th': {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell colSpan={3}>
                                  <Typography
                                    variant="body1"
                                    textAlign={'center'}
                                    fontStyle={'italic'}
                                    mt={2}
                                    mb={2}
                                  >
                                    No haunts to display
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              ghostChallengesSent?.map((game) => (
                                <Tooltip
                                  title={gameInfoTooltip(game, false)}
                                  placement="right"
                                >
                                  <TableRow
                                    hover
                                    key={game.gameId}
                                    sx={{
                                      '&:last-child td, &:last-child th': {
                                        border: 0,
                                      },
                                    }}
                                  >
                                    <TableCell>
                                      <IconButton
                                        onClick={(e) =>
                                          handleUsernameClick(
                                            e,
                                            game.ghostChallengeData.toProfileId
                                          )
                                        }
                                      >
                                        <Avatar
                                          src={resizeImage(
                                            game.ghostChallengeData
                                              .toAvatarPublicId,
                                            50,
                                            50
                                          )}
                                          variant="rounded"
                                          sx={{
                                            margin: '2px',
                                            height: 35,
                                            width: 35,
                                          }}
                                        />
                                      </IconButton>
                                    </TableCell>
                                    <TableCell>
                                      {game.ghostChallengeData
                                        .convertedToSolo ? (
                                        'Expired'
                                      ) : game.ghostChallengeData
                                          .completedAt ? (
                                        'Completed'
                                      ) : (
                                        <Countdown
                                          date={
                                            game.ghostChallengeData.expiresAt
                                          }
                                          renderer={countdownRenderer}
                                          onComplete={handleExpireComplete}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {game.ghostChallengeData.convertedToSolo
                                        ? 'Converted to Solo'
                                        : game.ghostChallengeData.completedAt
                                        ? results(game)
                                        : 'Pending'}
                                    </TableCell>
                                  </TableRow>
                                </Tooltip>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </StyledPaperItem>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={8} className="CONTAINER-RIGHT">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} className="PREV-GHOST-CHALLENGES">
                    <StyledPaperItem style={{ height: '100%' }}>
                      <Typography variant="h6" mb={2}>
                        Previous Ghost Challenges
                      </Typography>
                      <Stack
                        direction="row"
                        justifyContent={'center'}
                        spacing={2}
                        sx={{ overflowX: 'auto' }}
                      >
                        {recentGhostChallenges?.map((g, i) => (
                          <Tooltip
                            title={prevGameInfoTooltip(g)}
                            placement="top"
                          >
                            <ButtonBase
                              onClick={() => handleViewResultsClick(g.gameId)}
                            >
                              <Badge
                                badgeContent={
                                  i < lastViewedChallengeIndex
                                    ? 'new'
                                    : undefined
                                }
                                color="primary"
                              >
                                <Stack
                                  sx={{
                                    padding: 1,
                                    cursor: 'pointer',
                                    border:
                                      i < lastViewedChallengeIndex
                                        ? '1px solid'
                                        : undefined,
                                    borderColor:
                                      i < lastViewedChallengeIndex
                                        ? theme.palette.dailies.main
                                        : undefined,
                                    borderRadius: '0.25rem',
                                    '&:hover': {
                                      backgroundColor:
                                        theme.palette.primary.dark,
                                    },
                                  }}
                                >
                                  <Avatar
                                    src={resizeImage(
                                      getOpponent(g)?.avatarPublicId || '',
                                      50,
                                      50
                                    )}
                                    variant="rounded"
                                    sx={{
                                      margin: '2px',
                                      height: 35,
                                      width: 35,
                                    }}
                                  />
                                  <Typography
                                    variant="h6"
                                    fontWeight={600}
                                    mt={1}
                                    sx={{
                                      color: g.tie
                                        ? 'none'
                                        : g.winner === viewProfile?.id
                                        ? theme.palette.underPar.main
                                        : theme.palette.error.main,
                                    }}
                                  >
                                    {g.tie
                                      ? 'Tie'
                                      : g.winner === viewProfile?.id
                                      ? 'W'
                                      : 'L'}
                                  </Typography>
                                </Stack>
                              </Badge>
                            </ButtonBase>
                          </Tooltip>
                        ))}
                      </Stack>
                    </StyledPaperItem>
                  </Grid>
                  <Grid item xs={12} sm={12} className="RECOMMENDATIONS">
                    <StyledPaperItem style={{ height: '100%' }}>
                      <Typography variant="h6" mb={1}>
                        Recommendations
                      </Typography>
                      <FormControl sx={{ marginBottom: 2 }}>
                        <RadioGroup
                          row
                          value={recommendationsFilter}
                          onChange={handleRecommendationsFilter}
                        >
                          <Tooltip
                            title={`Active ghost challengers that haven't been challenged in a while`}
                            placement="top"
                          >
                            <FormControlLabel
                              value="been-a-while"
                              control={<Radio size="small" />}
                              label="Been a while"
                            />
                          </Tooltip>
                          <Tooltip
                            title={`Active ghost challengers similarily matched to you.`}
                            placement="top"
                          >
                            <FormControlLabel
                              value="similarily-matched"
                              control={<Radio size="small" />}
                              label="Similarily matched"
                            />
                          </Tooltip>
                          <Tooltip
                            title={`The most active ghost challengers that send out a ton of challenges and are pretty much guaranteed to respond to yours!`}
                            placement="top"
                          >
                            <FormControlLabel
                              value="active-haunters"
                              control={<Radio size="small" />}
                              label="Active haunters"
                            />
                          </Tooltip>
                          <Tooltip
                            title={`Players that haven't set their statuses to "YES", but have played a few ghosts challenges. Maybe they just need a nudge!`}
                            placement="top"
                          >
                            <FormControlLabel
                              value="maybes"
                              control={<Radio size="small" />}
                              label={`The "maybes"`}
                            />
                          </Tooltip>
                        </RadioGroup>
                      </FormControl>
                      <Stack
                        direction="row"
                        spacing={{ xs: 2, sm: 8 }}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          overflow: 'auto',
                        }}
                      >
                        {ghostRecommendations?.map((u) => (
                          <Card
                            variant="outlined"
                            // key={r.gameId}
                            sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              backgroundColor: '#222222',
                              minWidth: 'fit-content',
                            }}
                          >
                            <CardHeader
                              avatar={
                                <Avatar
                                  src={resizeImage(u.avatarPublicId, 80, 80)}
                                  variant="rounded"
                                  sx={{
                                    margin: '2px',
                                    height: 50,
                                    width: 50,
                                  }}
                                />
                              }
                              title={u.username}
                              sx={{ textAlign: 'left' }}
                            />
                            <CardContent sx={{ pt: 0 }}>
                              <Stack spacing={0.5}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: theme.palette.ranked.main }}
                                >
                                  MMR:{' '}
                                  <strong>
                                    {u.calibrated
                                      ? Math.floor(u.rankedRating)
                                      : 'calibrating'}
                                  </strong>
                                </Typography>
                                <Tooltip
                                  title="High response rate means this player responds to most haunts sent to them"
                                  placement="top"
                                >
                                  <Typography variant="caption">
                                    Response rate:{' '}
                                    <strong>
                                      {u.ghostChallengeResponseRate
                                        ? u.ghostChallengeResponseRate * 100 +
                                          '%'
                                        : '--'}
                                    </strong>
                                  </Typography>
                                </Tooltip>
                                <Tooltip
                                  title="A haunt ratio of 50% means they send and received the exact same number of haunts. A low haunt ratio means they send more haunts than they receive"
                                  placement="top"
                                >
                                  <Typography variant="caption">
                                    Haunt ratio:{' '}
                                    <strong>
                                      {(
                                        u.ghostChallengeReceivedSentRatio * 100
                                      ).toFixed(0)}
                                      %
                                    </strong>
                                  </Typography>
                                </Tooltip>
                                <Typography variant="caption">
                                  Total haunts:{' '}
                                  <strong>{u.ghostChallengesPlayed}</strong>
                                </Typography>
                                <Typography variant="caption">
                                  Last haunt received:{' '}
                                  <strong>
                                    {u.ghostChallengeLastReceivedAt
                                      ? timeSince(
                                          new Date(
                                            u.ghostChallengeLastReceivedAt
                                          )
                                        )
                                      : 'never'}
                                  </strong>
                                </Typography>
                                <Typography variant="caption">
                                  Requesting haunts:{' '}
                                  <strong>
                                    {u.ghostChallengeStatus || 'maybe'}
                                  </strong>
                                </Typography>
                              </Stack>
                            </CardContent>
                            <CardActions
                              sx={{ justifyContent: 'center', pb: 2 }}
                            >
                              <Button
                                variant="contained"
                                onClick={() =>
                                  handleGhostChallenge({
                                    profileId: u.id,
                                    username: u.username,
                                    ghostChallengeStatus:
                                      u.ghostChallengeStatus,
                                  })
                                }
                                disabled={
                                  maintenance.scheduled ||
                                  !!adminViewSelectedUser
                                }
                              >
                                Play
                              </Button>
                            </CardActions>
                          </Card>
                        ))}
                      </Stack>
                    </StyledPaperItem>
                  </Grid>
                  <Grid item xs={12} sm={12} className="WIN-RATES">
                    <StyledPaperItem style={{ height: '100%' }}>
                      <Typography variant="h6" mb={2}>
                        Win Rates
                      </Typography>
                      <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={2}
                      >
                        <TableContainer component={Paper}>
                          <Table aria-label="simple table" size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Player</TableCell>
                                <TableCell>Matches</TableCell>
                                <TableCell>Win Rate</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {ghostChallengeRecords
                                ?.slice(
                                  0,
                                  Math.ceil(ghostChallengeRecords.length / 2)
                                )
                                .map((record) => (
                                  <TableRow
                                    hover
                                    key={record.opponentProfileId}
                                    onMouseEnter={() =>
                                      setWinRatePlayId(record.opponentProfileId)
                                    }
                                    onMouseLeave={() =>
                                      setWinRatePlayId(undefined)
                                    }
                                    sx={{
                                      '&:last-child td, &:last-child th': {
                                        border: 0,
                                      },
                                    }}
                                  >
                                    <TableCell>
                                      <Stack direction={'row'} spacing={2}>
                                        <Avatar
                                          src={resizeImage(
                                            record.opponentAvatarPublicId,
                                            50,
                                            50
                                          )}
                                          variant="rounded"
                                          sx={{
                                            margin: '2px',
                                            height: 25,
                                            width: 25,
                                          }}
                                        />
                                        <Link
                                          component="button"
                                          variant="body2"
                                          onClick={(e) =>
                                            handleUsernameClick(
                                              e,
                                              record.opponentProfileId
                                            )
                                          }
                                          textAlign={'left'}
                                        >
                                          <Typography
                                            sx={{
                                              lineHeight: '25px',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              width: '150px',
                                            }}
                                          >
                                            {' '}
                                            {record.opponentUsername}
                                          </Typography>
                                        </Link>
                                      </Stack>
                                    </TableCell>
                                    {winRatePlayId ===
                                    record.opponentProfileId ? (
                                      <TableCell colSpan={2}>
                                        <Button
                                          variant="contained"
                                          fullWidth
                                          sx={{ height: '25px' }}
                                          onClick={() =>
                                            handleGhostChallenge({
                                              profileId:
                                                record.opponentProfileId,
                                              username: record.opponentUsername,
                                              ghostChallengeStatus:
                                                record.opponentGhostChallengeStatus,
                                            })
                                          }
                                          disabled={
                                            maintenance.scheduled ||
                                            !!adminViewSelectedUser ||
                                            record.opponentGhostChallengeStatus ===
                                              'no'
                                          }
                                        >
                                          Play
                                        </Button>
                                      </TableCell>
                                    ) : (
                                      <>
                                        <TableCell>
                                          {record.wins +
                                            record.losses +
                                            record.ties}
                                          <LinearProgress
                                            variant="determinate"
                                            value={getProgressValue(record)}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          {roundToOneDecimal(
                                            (record.wins /
                                              (record.wins +
                                                record.losses +
                                                record.ties)) *
                                              100
                                          )}
                                          %
                                          <LinearProgress
                                            variant="determinate"
                                            value={roundToOneDecimal(
                                              (record.wins /
                                                (record.wins +
                                                  record.losses +
                                                  record.ties)) *
                                                100
                                            )}
                                            color={'success'}
                                          />
                                        </TableCell>
                                      </>
                                    )}
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Divider orientation="vertical" flexItem />
                        <TableContainer component={Paper}>
                          <Table aria-label="simple table" size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Player</TableCell>
                                <TableCell>Matches</TableCell>
                                <TableCell>Win Rate</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {ghostChallengeRecords
                                ?.slice(
                                  Math.ceil(ghostChallengeRecords.length / 2),
                                  ghostChallengeRecords.length
                                )
                                .map((record) => (
                                  <TableRow
                                    hover
                                    key={record.opponentProfileId}
                                    onMouseEnter={() =>
                                      setWinRatePlayId(record.opponentProfileId)
                                    }
                                    onMouseLeave={() =>
                                      setWinRatePlayId(undefined)
                                    }
                                    sx={{
                                      '&:last-child td, &:last-child th': {
                                        border: 0,
                                      },
                                    }}
                                  >
                                    <TableCell>
                                      <Stack direction={'row'} spacing={2}>
                                        <Avatar
                                          src={resizeImage(
                                            record.opponentAvatarPublicId,
                                            50,
                                            50
                                          )}
                                          variant="rounded"
                                          sx={{
                                            margin: '2px',
                                            height: 25,
                                            width: 25,
                                          }}
                                        />
                                        <Link
                                          component="button"
                                          variant="body2"
                                          onClick={(e) =>
                                            handleUsernameClick(
                                              e,
                                              record.opponentProfileId
                                            )
                                          }
                                          textAlign={'left'}
                                        >
                                          <Typography
                                            sx={{
                                              lineHeight: '25px',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              width: '150px',
                                            }}
                                          >
                                            {' '}
                                            {record.opponentUsername}
                                          </Typography>
                                        </Link>
                                      </Stack>
                                    </TableCell>
                                    {winRatePlayId ===
                                    record.opponentProfileId ? (
                                      <TableCell colSpan={2}>
                                        <Button
                                          variant="contained"
                                          fullWidth
                                          sx={{ height: '25px' }}
                                          onClick={() =>
                                            handleGhostChallenge({
                                              profileId:
                                                record.opponentProfileId,
                                              username: record.opponentUsername,
                                              ghostChallengeStatus:
                                                record.opponentGhostChallengeStatus,
                                            })
                                          }
                                          disabled={
                                            maintenance.scheduled ||
                                            !!adminViewSelectedUser ||
                                            record.opponentGhostChallengeStatus ===
                                              'no'
                                          }
                                        >
                                          Play
                                        </Button>
                                      </TableCell>
                                    ) : (
                                      <>
                                        <TableCell>
                                          {record.wins +
                                            record.losses +
                                            record.ties}
                                          <LinearProgress
                                            variant="determinate"
                                            value={getProgressValue(record)}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          {roundToOneDecimal(
                                            (record.wins /
                                              (record.wins +
                                                record.losses +
                                                record.ties)) *
                                              100
                                          )}
                                          %
                                          <LinearProgress
                                            variant="determinate"
                                            value={roundToOneDecimal(
                                              (record.wins /
                                                (record.wins +
                                                  record.losses +
                                                  record.ties)) *
                                                100
                                            )}
                                            color={'success'}
                                          />
                                        </TableCell>
                                      </>
                                    )}
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Stack>
                    </StyledPaperItem>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            size="large"
            onClick={handleDialogClose}
            fullWidth
          >
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
